import { runRenderPipeline } from 'modules/manager/domain/render';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetPlayerStatsRenderContextQuery } from 'modules/manager/store/stat-render-api';
import { applyPlayerRankingOverrides } from 'modules/manager/domain/player-stats';
import { TemplateType } from 'modules/manager/domain/template-type';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import {
  HeadlessRenderCanvas,
  RenderErrorBoundary,
  StatusOverlay,
  collectBgUrls,
  loadCanvasJson,
  makeStatusTitle,
  nextFrame,
  parseId,
  toUserMessage,
} from './render-shared';
import { useRenderPageState } from './use-render-page-state';

const statusTitle = makeStatusTitle({
  loading: 'Loading template and championship data…',
  rendering: 'Rendering player statistics…',
});

const StatRenderPageInner: React.FC = () => {
  const [params] = useSearchParams();

  const championshipId = parseId(params.get('championshipId'));
  const templateId = parseId(params.get('templateId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);

  // Unlike the standings route, the context query cannot fire on the URL alone:
  // the ranking (which stat, which direction, how many slots) lives on the
  // template, and it is part of the query arg because it changes the result.
  // The template's ranking is the base; ?orderBy / ?order / ?top override it
  // per render (each absent param inherits the template's value), so the merged
  // ranking — not just the template's — becomes the query/cache key.
  const ranking = useMemo(
    () => (activeTemplate?.playerRanking ? applyPlayerRankingOverrides(activeTemplate.playerRanking, params) : undefined),
    [activeTemplate?.playerRanking, params],
  );
  const ctxQuery = useGetPlayerStatsRenderContextQuery(championshipId !== null && ranking ? { championshipId, ranking } : skipToken);

  const { editor, setEditor, status, setStatus, errorMessage, setErrorMessage, links, setLinks, orchestrated } = useRenderPageState({
    templateId,
    missingParamMessage: championshipId === null ? 'Missing championshipId in URL' : null,
    contextError: ctxQuery.error,
    contextErrorFallback: 'Failed to load player statistics render context',
  });

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data || championshipId === null) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    if (activeTemplate.templateType !== TemplateType.TOP_PLAYERS_BY_INDICATORS) {
      setStatus('error');
      setErrorMessage('Template is not a Статистика игроков template');
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
        await runRenderPipeline(editor, ctxQuery.data!, {
          bgUrls,
          templateType: activeTemplate.templateType,
          templateSubtypes: activeTemplate.templateSubtypes,
          layout: { settings: activeTemplate.matchLayout, authoredObjects: activeTemplate.objects ?? [] },
        });
        await nextFrame();
        const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });

        const saved = await mediaManagerRepository.persistChampionshipRenderedImage({
          championshipId,
          templateId: activeTemplate.id,
          dataUrl,
        });
        setLinks([{ url: saved.imageUrl, label: 'Player statistics' }]);
        setStatus('done');
      } catch (err) {
        console.error(err);
        setStatus('error');
        setErrorMessage(toUserMessage(err));
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, championshipId, bgUrls, orchestrated, setStatus, setErrorMessage, setLinks]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <HeadlessRenderCanvas template={activeTemplate} editor={editor} onReady={setEditor} />
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} links={links} />
    </>
  );
};

const StatRenderPage: React.FC = () => (
  <RenderErrorBoundary>
    <StatRenderPageInner />
  </RenderErrorBoundary>
);

export default StatRenderPage;
